// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import {
  getCreateAccountPayload,
  getApplyNowPayload,
  getLearnMorePayload,
  setLoyaltyLocation,
  getLoginPayload,
} from '@tcp/core/src/constants/analytics';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Button from '@tcp/core/src/components/common/atoms/Button';
import LoginPageDynamicStore from '@tcp/core/src/components/features/account/LoginPage/container/LoginPageDynamicStore';
import { routerPush, configureInternalNavigationFromCMSUrl, isClient } from '@tcp/core/src/utils';
import ClickTracker from '@tcp/web/src/components/common/atoms/ClickTracker';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import styles from '../FooterNavLinksList.style';

const TractOrderLinkPath = '/track-order';

/**
 *
 * @param {string} className set the classname for styling purpose.
 * @param {Object} listArray link list array.
 * @param {number} colnum col number.
 * @param {boolean} isLoggedIn to check if user is logged in or not.
 * @param {Object} linkConfig all the footer link action mappings
 */
const FooterNavLinksList = ({
  className,
  listArray,
  colNum,
  isLoggedIn,
  linkConfig,
  footerActionCreator,
  fullPageAuthEnabled,
  isFooterRedesignEnabled,
}) => {
  const trackOrderLink = (e) => {
    e.preventDefault();
    if (!isLoggedIn) routerPush(TractOrderLinkPath, TractOrderLinkPath);
    else routerPush('/account?id=orders', '/account/orders');
  };

  const loginModalOpenClick = async (e, handler) => {
    await LoginPageDynamicStore();
    e.preventDefault();
    footerActionCreator(handler, { state: true });
  };

  const logout = (e, handler) => {
    e.preventDefault();
    footerActionCreator(handler);
  };

  const myAccountLogin = (e, handler) => {
    e.preventDefault();
    // TO-DO : When the header is fixed at top, we can remove the scroll page call.
    const { pathname = 'referrer' } = (window && window.url) || {};
    if (fullPageAuthEnabled) {
      routerPush(`/home?target=login&successtarget=${pathname}`, '/home/login');
    } else {
      footerActionCreator(handler, {
        component: 'login',
        variation: 'primary',
      });
    }
  };

  const createNewAccount = (e, handler) => {
    e.preventDefault();
    const { pathname = 'referrer' } = (window && window.url) || {};
    if (fullPageAuthEnabled) {
      routerPush(`/home?target=register&successtarget=${pathname}`, '/home/register');
    } else {
      footerActionCreator(handler, {
        component: 'createAccount',
        variation: 'primary',
      });
    }
  };

  const createAccountOnClick = (action, linkItems, dispatchFn, onClick) => {
    let onClickVal = onClick;
    if (action === 'create-account' || linkItems.url === '/create-account')
      onClickVal = (e) => createNewAccount(e, dispatchFn);
    return onClickVal;
  };

  /**
   * @function shouldLoginModalOpen
   * @param {string} action - link action type
   * @param {string} URL -  Location to redirect
   */
  const shouldLoginModalOpen = (action, URL) => {
    let response = false;
    if (
      action === 'favorites' ||
      action === 'redeem-rewards' ||
      action === 'check-point-balance' ||
      action === 'track-order' ||
      URL === '/redeem-rewards' ||
      URL === '/check-point-balance' ||
      URL === '/favorites' ||
      URL === '/track-order'
    ) {
      response = true;
    }
    return response;
  };

  /**
   * Callback for redux action mapped to link action type
   * @callback dispatchFn
   * @param {Object} payload
   */

  /**
   * @function getOnClickAction
   * @param {string} action - link action type
   * @param {dispatchFn} dispatchFn -  redux action mapped to link action type
   */
  const getOnClickAction = (action, dispatchFn, linkItems) => {
    let onClick = null;
    if (action === 'track-order' || linkItems.url === TractOrderLinkPath) {
      onClick = (e) => trackOrderLink(e);
    } else if (action === 'favorites' || linkItems.url === '/favorites') {
      onClick = (e) => loginModalOpenClick(e, dispatchFn);
    } else if (action === 'log-out' || linkItems.url === '/log-out') {
      onClick = (e) => logout(e, dispatchFn);
    } else if (
      /*
      Don't move below or conditions of login-account inside the shouldLoginModalOpen function.
      As this function also used below in this file to decide whether text should work as link or button.
      But for login-account action text will always be button.
    */
      action === 'login-account' ||
      linkItems.url === '/login-account' ||
      shouldLoginModalOpen(action, linkItems.url)
    ) {
      onClick = (e) => myAccountLogin(e, dispatchFn);
    }

    onClick = createAccountOnClick(action, linkItems, dispatchFn, onClick);
    return onClick;
  };

  const hideLogoutMyActLinkBool = (linkItems) => {
    return (
      (isLoggedIn &&
        (linkItems.action === 'login-account' || linkItems.url === '/login-account')) ||
      (!isLoggedIn && (linkItems.action === 'log-out' || linkItems.url === '/log-out')) ||
      (isLoggedIn && (linkItems.action === 'create-account' || linkItems.url === '/create-account'))
    );
  };

  /**
   *
   * @param {bollean} isModal
   * @param {boolean} isAlreadyLoggedIn
   *
   * For action favorites, check-point-balance, redeem-rewards if user is logged in then user should navigate
   * to the cms configured location else login modal should open
   */
  const shouldLinkOrButton = (isModal) => {
    return !(isModal && isLoggedIn);
  };

  const globalFooterText = 'global-footer';

  const getLoyaltyPayload = (itemName) => {
    if (!itemName) return {};

    const navName = itemName.toLowerCase();

    if (navName === 'create an account' || navName === 'account')
      return getCreateAccountPayload(globalFooterText);
    if (navName === 'apply now') return getApplyNowPayload(globalFooterText);
    if (navName === 'learn more') return getLearnMorePayload(globalFooterText);
    if (navName === 'my account' || navName === 'manage your account')
      return getLoginPayload(globalFooterText);

    return {};
  };

  const footerPayload = (navItem) => ({
    ...getLoyaltyPayload(navItem),
    eventName: 'Navigation',
    pageNavigationText: `${globalFooterText}-${navItem}`,
  });

  const getLinkHref = (linkAction, linkUrl) => {
    return linkAction || linkUrl;
  };

  const getHandlers = (linkItems) => {
    const linkAction = linkItems.action;
    const linkUrl = linkItems.url;
    let dispatchFn = linkConfig[linkUrl] || linkConfig[linkAction] || null;
    const hideLogoutMyActLink = hideLogoutMyActLinkBool(linkItems) || false;
    const onClick = getLinkHref(linkAction, linkUrl)
      ? getOnClickAction(linkAction, dispatchFn, linkItems)
      : null;

    const isModal = shouldLoginModalOpen(linkAction, linkUrl);
    dispatchFn = isModal ? shouldLinkOrButton(isModal) : dispatchFn;
    return { hideLogoutMyActLink, onClick, dispatchFn };
  };

  const createNavListItem = (linkItems, index) => {
    /*
      hideLogoutMyActLink - true - if linkAction is login-account and user is logged in.
      hideLogoutMyActLink - true - if linkAction is log-out and user is  not logged in.
       hideLogoutMyActLink - false - other wise false, to show the other links.
      This condition is to satisfy the use case, to toggle between My Account and logout
      footer links.
      Use Case - When user is logged in, only Logout link should be visible to user.
                When user is not logged in, only My Account link should be visible to user.
      Problem Scenario - As the footer links are statically configured in CMS, if this condition
              is not put, user will be seeing both the links all the time.
    */
    const { hideLogoutMyActLink, onClick, dispatchFn } = getHandlers(linkItems);
    const { url: ctaUrl, target, title, actualUrl } = linkItems;
    const to = actualUrl || configureInternalNavigationFromCMSUrl(ctaUrl);

    /**
     * Resetting again dispatchFn based on user login state and link.
     * For favorites, check-point-balance, redeem-rewards if user is logged in then user should navigate
     * to the cms configured location else login modal should open
     */

    if (hideLogoutMyActLink) return null;

    /**
     * While toggling from one brand to another, we are replacing the window.history and while
     * applying for PLCC card from footer, the flow is getting affected because wrong window.history
     * doesn't work in that case.
     * So, we are making sure that the redirection happens to homepage when user toggles from another
     * brand and tries to Apply PLCC.
     * These changes are specific to APPLY PLCC ONLY and doesn't affect any other flow.
     * This is specific to home / login / register pages.
     */

    let newCtaUrl = ctaUrl;

    const pathName = (isClient() && window?.location?.pathname) || '';
    if (
      newCtaUrl === `/place-card/application` &&
      (pathName.endsWith('/home') || pathName.indexOf('/home/') > 0)
    ) {
      newCtaUrl += '?successtarget=home';
    }
    const trackAdobeAnalytics = (value) => {
      pushEventToAdobeAnalytics('footer_click', value);
    };
    return (
      <li>
        {dispatchFn !== null ? (
          <ClickTracker
            onClick={() => {
              setLoyaltyLocation(globalFooterText);
              trackAdobeAnalytics(linkItems?.text);
            }}
            clickData={footerPayload(linkItems.title)}
          >
            <Button
              type="button"
              data-locator={`col_${colNum}_link_${index}`}
              onClick={(e) => {
                setTimeout(() => {
                  onClick(e);
                }, 0);
              }}
              link
              title={linkItems.title}
            >
              {linkItems.text}
            </Button>
          </ClickTracker>
        ) : (
          <ClickTracker
            clickData={getLoyaltyPayload(linkItems.title)}
            onClick={() => {
              setLoyaltyLocation(globalFooterText);
              trackAdobeAnalytics(linkItems?.text);
            }}
          >
            <Anchor
              className={className}
              anchorVariation="primary"
              fontSizeVariation="large"
              dataLocator={`col_${colNum}_link_${index}`}
              to={to}
              asPath={newCtaUrl}
              target={target}
              title={title}
              rel={linkItems.text === 'Customer Support' && 'nofollow'}
              useRouterNavigation
            >
              {linkItems.text}
            </Anchor>
          </ClickTracker>
        )}
      </li>
    );
  };

  const renderFooterNavigation = () => {
    if (isFooterRedesignEnabled) {
      return (
        <>
          <ul className={`${className} list flex-container`}>
            {listArray.map((linkItems, index) => createNavListItem(linkItems, index))}
          </ul>
          <div className={`${className} divider `} />
        </>
      );
    }
    return (
      <ul className={`${className} list`}>
        {listArray.map((linkItems, index) => createNavListItem(linkItems, index))}
      </ul>
    );
  };

  return renderFooterNavigation();
};

FooterNavLinksList.propTypes = {
  className: PropTypes.string.isRequired,
  listArray: PropTypes.shape([]).isRequired,
  colNum: PropTypes.number.isRequired,
  isLoggedIn: PropTypes.bool,
  linkConfig: PropTypes.shape({
    'track-order': PropTypes.func,
    favorites: PropTypes.func,
    'log-out': PropTypes.func,
    'login-account': PropTypes.func,
    'create-account': PropTypes.func,
  }).isRequired,
  footerActionCreator: PropTypes.func.isRequired,
  defaultStore: PropTypes.shape({}),
  fullPageAuthEnabled: PropTypes.bool,
  isFooterRedesignEnabled: PropTypes.bool,
};

FooterNavLinksList.defaultProps = {
  isLoggedIn: false,
  defaultStore: {},
  fullPageAuthEnabled: false,
  isFooterRedesignEnabled: false,
};

export default withStyles(FooterNavLinksList, styles);

export { FooterNavLinksList as FooterNavLinksListVanilla };
